import type { LucideIcon } from "lucide-react"

export enum ChatScope {
  Global = "Global",
  Deal = "Deal",
  Document = "Document",
}

export type ChatLayoutMode = "dock" | "sidebar" | "modal"
export type ChatDockAnchor = "left" | "center" | "right"
export type ChatPanelSize = "medium" | "large"

export type ContextItemType = "contract" | "document" | "person" | "folder" | "page"

export interface ChatContextItem {
  id: string
  type: ContextItemType
  label: string
  value: string
  description?: string
  // Legacy compatibility fields for older chat UI call sites.
  detail?: string
  scope?: ChatScope.Deal | ChatScope.Document
}

export type MessageActionType =
  | "navigate"
  | "highlight"
  | "rating"
  | "copy"
  | "feedback"
  | "custom"

export interface MessageAction {
  id: string
  type: MessageActionType
  label: string
  icon?: LucideIcon
  variant?: "default" | "secondary" | "outline" | "ghost"
  data?: unknown
  onAction?: (action: MessageAction) => void | Promise<void>
}

export interface NavigateAction extends MessageAction {
  type: "navigate"
  data: {
    route?: string
    viewId?: string
    scrollTo?: string
    highlight?: boolean
  }
}

export interface HighlightAction extends MessageAction {
  type: "highlight"
  data: {
    targetId: string
    duration?: number
  }
}

export interface RatingAction extends MessageAction {
  type: "rating"
  data: {
    messageId: string
    value?: "positive" | "negative"
  }
}

export interface CopyAction extends MessageAction {
  type: "copy"
  data: {
    text: string
  }
}

/**
 * #111 P4b — rich attachment shape for chat messages.
 *
 * Mirrors the shape api-server's chat handler returns when an inbound
 * message references a file (PDF preview, audio voice memo, image
 * snippet, etc.). The renderer in `MessageAttachmentRenderer` switches
 * on `mimeType` to pick an inline preview; anything unknown falls
 * through to a download chip.
 *
 * `url` is a signed S3 URL with a short TTL — the api-server stamps
 * one when the message is materialized. Not stable for caching across
 * renders longer than the URL's TTL.
 */
export interface MessageAttachment {
  id: string
  fileName: string
  /** Canonical MIME type (e.g. `application/pdf`, `image/png`, `audio/mpeg`). */
  mimeType: string
  /** Signed S3 URL — short-TTL, do not persist client-side. */
  url: string
  sizeBytes?: number
  /** Optional inline thumbnail URL (already signed) for image previews. */
  thumbnailUrl?: string
}

export interface Message {
  id: string
  role: "user" | "assistant"
  content: string
  timestamp: Date
  actions?: MessageAction[]
  scope?: ChatScope
  contextItems?: ChatContextItem[]
  /**
   * Attachments associated with this message. Populated from the chat
   * API on inbound assistant messages (Ari forwards email/iMessage
   * attachments through here) and on user uploads via the composer.
   * Populating from the API response is the open work in #111 P4b;
   * the renderer here is ready for it.
   */
  attachments?: MessageAttachment[]
}

export interface ChatSession {
  id: string
  title: string
  summary?: string
  messages: Message[]
  scope: ChatScope
  createdAt: Date
  updatedAt: Date
  draft: string
  contextItems: ChatContextItem[]
}

export interface SuggestedPrompt {
  label: string
  scope: ChatScope
}

export interface ChatLayoutPreferences {
  mode: ChatLayoutMode
  dockAnchor: ChatDockAnchor
  panelSize: ChatPanelSize
}

let actionCounter = 0

export function createNavigateAction(
  label: string,
  route: string,
  options?: Partial<NavigateAction["data"]>
): NavigateAction {
  return {
    id: `nav-${Date.now()}-${actionCounter++}`,
    type: "navigate",
    label,
    variant: "default",
    data: {
      route,
      ...options,
    },
  }
}

export function createRatingAction(messageId: string): RatingAction {
  return {
    id: `rating-${messageId}`,
    type: "rating",
    label: "Rate this response",
    variant: "ghost",
    data: {
      messageId,
    },
  }
}

export function createCopyAction(text: string): CopyAction {
  return {
    id: `copy-${Date.now()}-${actionCounter++}`,
    type: "copy",
    label: "Copy",
    variant: "ghost",
    data: {
      text,
    },
  }
}

// TODO(api-model): These types mirror ChatToolProgress / ChatThinkingState
// from sse.smithy. Once the api-model package is the authoritative source
// in production code, the domain types below can become projections that
// import from the api-model layer. For now they are frontend prototypes.

export interface ChatToolProgress {
  toolName: string
  toolLabel: string
  durationMs?: number
  error?: string
}

export interface ChatThinkingState {
  phase: "reasoning" | "planning" | "summarizing" | "tool_routing"
  label: string
}

export type ChatProgressFrameKind = "typing" | "tool_start" | "tool_done"

export interface ChatProgressFrame {
  kind: ChatProgressFrameKind
  toolProgress?: ChatToolProgress
  thinkingState?: ChatThinkingState
  timestamp: Date
}
